import {
  Center,
  Flex,
  Grid,
  HStack,
  Popover,
  Portal,
  Spinner,
  Text
} from "@chakra-ui/react"
import Link, {
  LinkHrefProps,
  LinkToProps
} from "@pathwright/ux/src/components/link/Link"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import { useMemo } from "react"
import {
  HomeRegistrationFragment,
  useHomeResourceGroupsQuery
} from "@pathwright/graphql-client/src/generated"
import Pathicon from "../../../pathicon/Pathicon"
import { getPathLink, getRegistrationPathLink } from "../../utils"
import { GroupAvatars } from "../GroupAvatars"
import SimpleButton, { SimpleButtonProps } from "../ui/SimpleButton"
import { PathCardSubtitle } from "./PathCard"

type SimpleButtonWrapperProps = Omit<SimpleButtonProps, "icon" | "label"> &
  (LinkToProps | LinkHrefProps)

const StartButton: typeof SimpleButton = (props) => {
  const { t } = useTranslate()
  return (
    <SimpleButton
      label={t("registration.status.start")}
      icon="chevron-right"
      {...props}
    />
  )
}

const ResumeButton: typeof SimpleButton = (props) => {
  const { t } = useTranslate()
  return (
    <SimpleButton
      label={t("registration.status.resume")}
      icon="chevron-right"
      {...props}
    />
  )
}

const CompleteButton: typeof SimpleButton = (props) => {
  const { t } = useTranslate()
  return (
    <SimpleButton
      label={t("registration.status.completed")}
      leftIcon="check"
      {...props}
    />
  )
}

const DesignSourceButton: typeof SimpleButton = (props) => {
  const { t } = useTranslate()
  return (
    <SimpleButton
      label={t("Source Path")}
      leftIcon="cloud-arrows-fill"
      {...props}
    />
  )
}

export const ProgressAction = ({
  progress,
  ...buttonProps
}: SimpleButtonWrapperProps & { progress?: number | null }) => {
  return progress ? (
    progress === 1 ? (
      <CompleteButton {...buttonProps} />
    ) : (
      // <ProgressBar progress={progress} />
      <ResumeButton {...buttonProps} />
    )
  ) : (
    <StartButton {...buttonProps} />
  )
}

const DesignWithCohortsActionContent = ({
  resourceId,
  cohortId,
  totalCohorts,
  to,
  ...buttonProps
}: SimpleButtonWrapperProps & {
  resourceId: number
  cohortId: number
  totalCohorts: number
}) => {
  // TODO: paginate or load all!
  const { data, loading, refetch } = useHomeResourceGroupsQuery({
    variables: {
      resourceId: resourceId
    },
    fetchPolicy: "cache-and-network"
  })

  const resource = data?.resource
  const groups = resource?.groups?.edges?.map((edge) => edge?.group!) || []

  // If any cohort has members, show the avatars column.
  const hasCohortWithMembers = useMemo(
    () => groups.some((group) => !!group.registrations?.total),
    [groups]
  )

  return (
    <>
      {!!groups.length ? (
        <Grid
          mb={2}
          templateColumns={
            hasCohortWithMembers
              ? "max-content minmax(0, 1fr)"
              : "minmax(0, 1fr)"
          }
        >
          {groups.map((group) => (
            <Link
              key={group.id}
              to={getPathLink({
                resourceSlug: resource?.slug!,
                cohortId: group.id,
                // Force design role when none specified.
                registrationRole: group.user_registration?.role || 20
              })}
              variant="plain"
              textDecor="none"
              p={2}
              pr={1}
              display="grid"
              // Using subgrid to ensure left avatar column width is uniform.
              gridTemplateColumns="subgrid"
              gridColumn="1 / -1"
              _hover={{ bg: "gray.100" }}
              borderRadius="button"
              overflow="hidden"
            >
              {hasCohortWithMembers && (
                <Flex
                  h="100%"
                  alignItems="flex-start"
                  justifyContent="flex-end"
                >
                  {!!group.registrations?.total && (
                    <GroupAvatars group={group} showGroupName={false} />
                  )}
                </Flex>
              )}
              <PathCardSubtitle lineClamp={2} color="inherit">
                {group.name}
              </PathCardSubtitle>
            </Link>
          ))}
        </Grid>
      ) : loading ? (
        <Center w="100%" py={6}>
          <Spinner />
        </Center>
      ) : null}
      <DesignSourceButton
        w="100%"
        colorPalette="gray"
        variant="subtle"
        to={to}
        position="sticky"
        bottom={0}
        {...buttonProps}
      />
    </>
  )
}

const DesignWithCohortsAction = ({
  resourceId,
  cohortId,
  totalCohorts,
  to,
  ...buttonProps
}: SimpleButtonWrapperProps & {
  resourceId: number
  cohortId: number
  totalCohorts: number
}) => {
  if (totalCohorts < 2) {
    return null
  }

  return (
    <Popover.Root lazyMount>
      <Popover.Trigger asChild>
        <SimpleButton
          leftIcon="group"
          colorPalette="whiteAlpha"
          variant="solid"
          {...buttonProps}
        >
          <HStack py={1} w="100%">
            <Text
              as="span"
              color="inherit"
              fontSize="sm"
              fontWeight="bold"
              m={0}
            >
              {totalCohorts} Cohorts
            </Text>
            <Pathicon icon={"chevron-down"} />
          </HStack>
        </SimpleButton>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content minW={["unset", "380px"]}>
            <Popover.Arrow />
            <Popover.Body overflow="auto" p={0} m="var(--popover-padding)">
              <DesignWithCohortsActionContent
                resourceId={resourceId}
                cohortId={cohortId}
                totalCohorts={totalCohorts}
                to={to}
                {...buttonProps}
              />
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

const PathCardAction = ({
  registration,
  ...buttonProps
}: {
  registration: HomeRegistrationFragment
} & Omit<SimpleButtonWrapperProps, "to">) => {
  const toPath = useMemo(
    () => getRegistrationPathLink(registration, { enableNextItem: true }),
    [registration]
  )

  const totalCohorts = registration.resource?.groups?.total || 0

  switch (registration.role) {
    case -1:
      return (
        <SimpleButton
          label="Guided Tour"
          icon="group"
          {...buttonProps}
          to={toPath}
        />
      )
    case 5:
    case 10:
      return (
        <ProgressAction
          progress={registration.path?.progress}
          {...buttonProps}
          to={toPath}
        />
      )
    case 15:
      return null
    case 20:
      return registration.group?.is_master ? (
        <DesignWithCohortsAction
          resourceId={registration.resource!.id}
          cohortId={registration.group.id}
          totalCohorts={totalCohorts}
          {...buttonProps}
          to={toPath}
        />
      ) : totalCohorts <= 1 ? (
        <DesignSourceButton {...buttonProps} to={toPath} />
      ) : null
  }
}

export default PathCardAction
